import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { WebApplicationConfigurationService } from '@app/shared/services/web-application-configuration.service';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthAuthService } from '../services/auth-auth.service';
import { AuthorizationService } from '../services/authorization.service';
import { ExternalApiModuleWrapper } from '../services/external.api.module.wrapper.service';
import { RedirectAfterLoginService } from '../services/redirect-after-login.service';

@Injectable({ providedIn: 'root' })
export class RouteGuard {
  private router = inject(Router);
  private authAuthService = inject(AuthAuthService);
  private authorizationService = inject(AuthorizationService);
  private externalApiModuleWrapper = inject(ExternalApiModuleWrapper);
  private webApplicationConfigurationService = inject(WebApplicationConfigurationService);
  private redirectAfterLoginService = inject(RedirectAfterLoginService);

  public async canActivate(activatedRoute: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Promise<boolean> {
    let canActivate = false;
    let isAuthenticated = this.authAuthService.hasValidAccessToken() && this.authAuthService.hasValidIdToken();
    if (environment.debug) {
      console.log(`isAuthenticated=${isAuthenticated}`);
    }
    const hasValidApiAccessToken: boolean = this.authAuthService.hasValidApiAccessToken();
    if (environment.debug) {
      console.log(`hasValidApiAccessToken=${hasValidApiAccessToken}`);
    }
    let scopes: string[] | undefined = this.authAuthService.scopes;
    if (environment.debug) {
      console.log(`scopes=${JSON.stringify(scopes)}`);
    }
    let scope: string | null = this.authAuthService.scope;
    if (environment.debug) {
      console.log(`scope=${scope}`);
    }
    try {
      if (!isAuthenticated) {
        await this.authAuthService.inAppLogin();
        isAuthenticated = this.authAuthService.hasValidAccessToken() && this.authAuthService.hasValidIdToken();
        if (environment.debug) {
          console.log(`isAuthenticated=${isAuthenticated}`);
        }
      }
      if (isAuthenticated && hasValidApiAccessToken) {
        await this.authAuthService.selectUserProfile();
      }
      if (isAuthenticated && !hasValidApiAccessToken) {
        const audience = (await firstValueFrom(this.webApplicationConfigurationService.getPublicPropertyValue('public.authorization-config.audience', undefined))) ?? undefined;
        await this.externalApiModuleWrapper.waitStsInitialized();
        scopes = await firstValueFrom(this.externalApiModuleWrapper.stsScopeService().searchScopes(undefined, audience));
        this.authAuthService.scopes = scopes ?? [];
        if (!scope) {
          await this.authAuthService.selectScope().then((result) => (scope = result));
        }
        if (scope && scopes?.includes(scope)) {
          let organisationFailToLoad = false;
          await this.authAuthService.exchangeToken();
          await this.authAuthService.selectUserProfile().catch((_err) => {
            organisationFailToLoad = true;
            this.authAuthService.logout('public/disabled-user');
          });
          if (organisationFailToLoad) {
            return new Promise((resolve) => resolve(false));
          }
        }
      }
    } catch (e) {
      console.error(e);
      this.authAuthService.invalidateApiAccessToken();
    }
    if (isAuthenticated && scope == null) {
      //something is wrong as scope can't be null
      this.authAuthService.logout();
    } else if (!isAuthenticated) {
      this.router.navigateByUrl('/public/login').then();
    } else if (scopes?.length === 0) {
      this.authAuthService.navigateToDisabledPage();
    } else if (!scope || !scopes?.includes(scope)) {
      console.error('Multiple scope management is not implemented.');
      this.authAuthService.logout();
      // this.authAuthService.bootstrapUrl = routerState.url;
      // this.authAuthService.navigateToScopePage();
    } else {
      canActivate = this.isRouteAllowed(activatedRoute.routeConfig);
    }
    if (environment.debug) {
      console.log(`${routerState.url}: canActivate=${canActivate}`);
    }
    if (!canActivate) {
      this.redirectAfterLoginService.routerUrl = routerState.url;
    }
    return new Promise((resolve) => resolve(canActivate));
  }

  public async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.canActivate(childRoute, state);
  }

  public isRouteAllowed(route: Route | null): boolean {
    const auth = route && route.data ? route.data.auth : undefined;
    return auth ? this.authorizationService.canAccess(auth.action) : true;
  }

  public canLoad(route: Route, _segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (route.data && route.data.options && route.data.options.onlyDev) {
      return environment.debug; // if prod = false it will load module
    }
    return true;
  }
}
